import React, { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { useEmployerAndPlans } from "../../../data/dashboardInightHub";
import { formatFirestoreTStoDate } from "./helper";

export default function SubscriptionOverviewSection() {
    const { data } = useEmployerAndPlans("employer_subscription_overview");

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 15;

    const lastUpdatedAt = formatFirestoreTStoDate(data?.updatedAt);

    /**
     * ---------------------------------------------
     * STABLE & CONSISTENT SORTING
     * ACTIVE → alphabetical
     * INACTIVE → alphabetical
     * ---------------------------------------------
     */
    const sortedEmployers = useMemo(() => {
        const employers = [...(data?.employers || [])];

        return employers.sort((a: any, b: any) => {
            // 1. ACTIVE always first
            if (a.status === "ACTIVE" && b.status !== "ACTIVE") return -1;
            if (b.status === "ACTIVE" && a.status !== "ACTIVE") return 1;

            // 2. Both active OR both not active → alphabetical order
            return a.name.localeCompare(b.name);
        });
    }, [data]);

    /**
     * -----------------------------
     * FILTERING BASED ON SEARCH
     * -----------------------------
     */
    const filtered = useMemo(() => {
        return sortedEmployers.filter((emp: any) =>
            emp.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [sortedEmployers, search]);

    /**
     * -----------------------------
     * PAGINATION
     * -----------------------------
     */
    const totalPages = Math.ceil(filtered.length / rowsPerPage);

    const paginated = useMemo(() => {
        return filtered.slice(
            (currentPage - 1) * rowsPerPage,
            currentPage * rowsPerPage
        );
    }, [filtered, currentPage]);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    const renderPagination = () => {
        const pages = [];
        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) pages.push(1, 2, 3, 4, "...", totalPages);
            else if (currentPage >= totalPages - 2)
                pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            else
                pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
        }

        return (
            <div className="flex justify-end space-x-2 mt-6">
                <button
                    className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    Prev
                </button>
                {pages.map((page, index) =>
                    page === "..." ? (
                        <span key={index} className="px-3 py-1 text-gray-500">...</span>
                    ) : (
                        <button
                            key={index}
                            className={`px-3 py-1 border rounded ${page === currentPage
                                    ? "bg-instant-teams-blue-Main text-white border-instant-teams-blue-Main"
                                    : "hover:bg-gray-100"
                                }`}
                            onClick={() => handlePageChange(Number(page))}
                        >
                            {page}
                        </button>
                    )
                )}
                <button
                    className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
            </div>
        );
    };

            function formatToUSDate(dateStr: string) {
            if (!dateStr || dateStr === "N/A") return "N/A";

            const [day, month, year] = dateStr.split("/");

            // basic validation
            if (!day || !month || !year) return "Invalid Date";

            return `${month}/${day}/${year}`;
        }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Subscriptions Status</h2>
                {lastUpdatedAt && (
                    <p className="text-instant-teams-blue-Main text-lg font-semibold">
                        {`Last updated: ${lastUpdatedAt}`}
                    </p>
                )}
            </div>

            <div className="flex justify-end mb-4">
                <div className="relative w-64">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search employers..."
                        value={search}
                        onChange={(e) => {
                            setCurrentPage(1);
                            setSearch(e.target.value);
                        }}
                        className="pl-9 w-full border rounded-md py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                </div>
            </div>

            <div className="overflow-hidden border rounded-lg">
                <table className="min-w-full text-sm text-gray-700">
                    <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                        <tr>
                            <th className="px-6 py-3 text-left font-semibold">Employer</th>
                            <th className="px-6 py-3 text-left font-semibold">Plan</th>
                            <th className="px-6 py-3 text-left font-semibold">Status</th>
                            <th className="px-6 py-3 text-left font-semibold">Next Billing / End Date</th>
                            <th className="px-6 py-3 text-left font-semibold">Credits Used/Total</th>
                        </tr>
                    </thead>

                    <tbody className="divide-y">
                        {paginated.map((emp: any) => (
                            <tr key={emp.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                    <div className="font-medium">{emp.name}</div>
                                    <div className="text-xs text-gray-500">
                                        {emp.email === "N/A" ? "" : emp.email}
                                    </div>
                                </td>

                                <td className="px-6 py-4">
                                    {emp.plan && (
                                        <span
                                            className={`px-2 py-1 rounded-full text-xs font-medium border ${emp.plan === "MONTHLY"
                                                    ? "text-blue-700 border-blue-300 bg-blue-50"
                                                    : emp.plan === "SINGLE PLAN"
                                                        ? "text-purple-700 border-purple-300 bg-purple-50"
                                                        : emp.plan === "CUSTOM ANNUAL"
                                                            ? "text-green-700 border-green-300 bg-green-50"
                                                            : emp.plan === "ANNUAL FLEX 12" || emp.plan === "ANNUAL FLEX 24"
                                                                ? "text-instant-teams-blue-D1 border-instant-teams-blue-Main bg-cyan-100"
                                                                : "text-gray-700 border-gray-300 bg-gray-50"
                                                }`}
                                        >
                                            {emp.plan}
                                        </span>
                                    )}
                                </td>

                                <td className="px-6 py-4">
                                    <span
                                        className={`px-2 py-1 rounded-full text-xs font-semibold ${emp.status === "ACTIVE"
                                                ? "bg-green-100 text-green-800"
                                                : "bg-red-100 text-red-700"
                                            }`}
                                    >
                                        {emp.status}
                                    </span>
                                </td>

                                <td className="px-6 py-4">{formatToUSDate(emp.nextBilling)}</td>

                                <td className="px-6 py-4">
                                    <div className="w-48">
                                        <div className="text-sm font-medium mb-1">
                                            {emp.credits}/{emp.totalCredits} credits
                                        </div>
                                        <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full ${emp.totalCredits === 0 && emp.credits === 0
                                                        ? "bg-gray-200"
                                                        : "bg-instant-teams-teal-D1"
                                                    }`}
                                                style={{
                                                    width:
                                                        emp.totalCredits === 0
                                                            ? "100%"
                                                            : `${(emp.credits / emp.totalCredits) * 100}%`,
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {renderPagination()}
        </div>
    );
}
